import React, { useState, useEffect } from 'react';
import { Search, Menu, X, ChevronUp, ChevronDown } from 'lucide-react';
import Button from '../../components/ui/Button';
import TableOfContents from './components/TableOfContents';
import PolicySection from './components/PolicySection';
import SearchHighlight from './components/SearchHighlight';
import PrintDownloadActions from './components/PrintDownloadActions';

const PrivacyPolicy = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const policyData = {
    effectiveDate: 'September 9, 2025',
    lastUpdated: 'September 9, 2025',
    company: 'Epoxy Dogs LLC',
    service: 'SkillBinder',
    sections: [
      {
        id: 'information-collection',
        title: '1. Information We Collect',
        content: `Personal Information: Name, email address, billing details, occupation details, and other information you provide when creating an account or using our contact form.

Usage Information: IP address, device type, browser, and how you interact with our platform.

Career Data (optional): Occupation, skills, career goals, ZIP code(s) for salary insights.`
      },
      {
        id: 'information-usage',
        title: '2. How We Use Your Information',
        content: `To provide and improve SkillBinder services.

To process payments and manage subscriptions.

To personalize your experience (e.g., occupation-based exercises, salary insights).

To communicate with you about updates, support, or promotional offers.

To comply with legal requirements.`
      },
      {
        id: 'information-sharing',
        title: '3. Sharing of Information',
        content: `We do not sell your personal information. We may share it only:

With trusted service providers (e.g., payment processors, email delivery platforms) who perform services on our behalf.

If required by law or to protect rights and safety.

In the event of a business transfer (e.g., acquisition).`
      },
      {
        id: 'data-retention',
        title: '4. Data Retention',
        content: `We keep your data as long as your account is active, or as needed to provide services. You may request deletion at any time.`
      },
      {
        id: 'security',
        title: '5. Security',
        content: `We use industry-standard security measures (encryption, authentication) to protect your information, but no system is 100% secure.`
      },
      {
        id: 'user-rights',
        title: '6. Your Rights',
        content: `Depending on your location, you may have rights to:

Access, correct, or delete your personal data.

Opt out of marketing emails.

Restrict or object to processing.`
      },
      {
        id: 'cookies-tracking',
        title: '7. Cookies & Tracking',
        content: `We may use cookies and analytics tools to improve our service. You can manage cookies in your browser settings.`
      },
      {
        id: 'childrens-privacy',
        title: '8. Children\'s Privacy',
        content: `SkillBinder is not intended for children under 13. We do not knowingly collect data from children.`
      },
      {
        id: 'contact-us',
        title: '9. Contact Us',
        content: `For privacy questions or requests, contact:
📧 support@skillbinder.com
📬 Epoxy Dogs LLC, 5698 Lamplighter Dr., Girard, OH 44420`
      }
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = policyData?.sections;
      let current = '';

      sections?.forEach(section => {
        const element = document.getElementById(section?.id);
        if (element) {
          const rect = element?.getBoundingClientRect();
          if (rect?.top <= 100) {
            current = section?.id;
          }
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [policyData?.sections]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const filteredSections = policyData?.sections?.filter(section =>
    searchTerm === '' || 
    section?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    section?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-sm text-gray-600 mt-1">
                Effective Date: {policyData?.effectiveDate} | Last Updated: {policyData?.lastUpdated}
              </p>
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              <PrintDownloadActions policyData={policyData} />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t bg-white">
            <div className="px-4 py-1 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search policy..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <PrintDownloadActions policyData={policyData} />
            </div>
          </div>
        )}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Desktop Table of Contents */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-8">
              <TableOfContents
                sections={policyData?.sections}
                activeSection={activeSection}
                onSectionClick={scrollToSection}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-8">
                {/* Policy Header */}
                <div className="mb-2 pb-6 border-b">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">📜 Privacy Policy</h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>{policyData?.company}</strong> ("we," "our," "us") operates{' '}
                      <strong>{policyData?.service}</strong> ("the Service"). We respect your privacy 
                      and are committed to protecting it. This Privacy Policy explains what information 
                      we collect, how we use it, and your rights.
                    </p>
                  </div>
                </div>

                {/* Policy Sections */}
                <div className="space-y-8">
                  {filteredSections?.map((section, index) => (
                    <div key={section?.id} className="lg:hidden">
                      {/* Mobile Accordion Style */}
                      <button
                        onClick={() => toggleSection(section?.id)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 text-left">
                          {section?.title}
                        </h3>
                        {expandedSections?.[section?.id] ? 
                          <ChevronUp size={20} className="text-gray-500" /> : 
                          <ChevronDown size={20} className="text-gray-500" />
                        }
                      </button>
                      
                      {expandedSections?.[section?.id] && (
                        <div className="mt-4 p-4 bg-white border rounded-lg">
                          <SearchHighlight text={section?.content} searchTerm={searchTerm} />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Desktop Full Display */}
                  <div className="hidden lg:block">
                    {filteredSections?.map((section, index) => (
                      <PolicySection
                        key={section?.id}
                        section={section}
                        searchTerm={searchTerm}
                        isActive={activeSection === section?.id}
                      />
                    ))}
                  </div>
                </div>

                {/* No Results */}
                {filteredSections?.length === 0 && (
                  <div className="text-center py-12">
                    <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                    <p className="text-gray-600">
                      Try adjusting your search terms or browse all sections above.
                    </p>
                    <Button
                      onClick={() => setSearchTerm('')}
                      variant="outline"
                      className="mt-4"
                    >
                      Clear Search
                    </Button>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-12 pt-8 border-t">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Last Updated:</strong> {policyData?.lastUpdated}
                      <br />
                      This policy may be updated periodically. We will notify users of any material changes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;